import { Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Menu,
  X,
  ChevronRight,
  Zap,
  Route,
  Factory,
  HelpCircle,
  Calculator,
  ArrowRight,
  Truck,
} from "lucide-react";
import isotipoSvg from "@assets/isotipo.svg";
import { SiWhatsapp } from "react-icons/si";
import {
  PHONE_NUMBER,
  PHONE_HREF,
  getWhatsAppUrl,
  trackWhatsAppClick,
  trackPhoneClick,
  EMAIL,
} from "@/lib/tracking";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  {
    href: "/",
    label: "Inicio",
    icon: Truck,
    desc: "Nuestra propuesta de valor",
  },
  {
    href: "/servicio-urgente",
    label: "Servicio Urgente",
    icon: Zap,
    desc: "Recogida rápida, entrega inmediata",
  },
  {
    href: "/servicio-estandar",
    label: "Servicio Estándar",
    icon: Route,
    desc: "Rutas habituales a 0,37 €/km",
  },
  {
    href: "/fabricantes",
    label: "Para Fabricantes",
    icon: Factory,
    desc: "Soluciones logísticas B2B",
  },
  {
    href: "/faq",
    label: "FAQ",
    icon: HelpCircle,
    desc: "Preguntas frecuentes",
  },
  {
    href: "/contacto",
    label: "Presupuesto",
    icon: Calculator,
    desc: "Calcula precio y pide presupuesto",
  },
];

const overlayVariants = {
  closed: { opacity: 0, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
  open: { opacity: 1, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
};

const menuContainerVariants = {
  closed: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
  open: { transition: { staggerChildren: 0.06, delayChildren: 0.15 } },
};

const menuItemVariants = {
  closed: {
    opacity: 0,
    x: -40,
    filter: "blur(4px)",
    transition: { duration: 0.2 },
  },
  open: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { duration: 0.35, ease: [0, 0, 0.2, 1] },
  },
};

const bottomBarVariants = {
  closed: { opacity: 0, y: 30, transition: { duration: 0.2 } },
  open: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: 0.35, ease: [0, 0, 0.2, 1] },
  },
};

function Header() {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b"
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 h-16">
            <Link href="/" data-testid="link-home-logo">
              <div className="flex items-center gap-2 cursor-pointer">
                <img
                  src={isotipoSvg}
                  alt="TDMA Logistica"
                  className="w-9 h-9"
                />
                <div className="flex flex-col">
                  <span className="font-display font-bold text-lg leading-tight tracking-tight">
                    TDMA
                  </span>
                  <span className="text-[10px] font-medium text-muted-foreground leading-tight uppercase tracking-widest">
                    Logistica
                  </span>
                </div>
              </div>
            </Link>

            <nav
              className="hidden lg:flex items-center gap-1"
              data-testid="nav-desktop"
            >
              {navLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  <span
                    className={`px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                      location === link.href
                        ? "text-primary bg-primary/5"
                        : "text-muted-foreground"
                    }`}
                    data-testid={`link-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </span>
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-2">
              <a
                href={PHONE_HREF}
                onClick={() => trackPhoneClick("header")}
                data-testid="link-phone-header"
              >
                <Button variant="outline" size="sm">
                  <Phone className="w-4 h-4 mr-1" />
                  {PHONE_NUMBER}
                </Button>
              </a>
              <Link href="/contacto">
                <Button size="sm" data-testid="button-cta-header">
                  Pedir Presupuesto
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <button
              className="lg:hidden relative z-[70] w-10 h-10 flex items-center justify-center rounded-md"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="button-mobile-menu"
              aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            >
              <AnimatePresence mode="wait">
                {mobileOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            variants={overlayVariants}
            initial="closed"
            animate="open"
            exit="closed"
            data-testid="nav-mobile"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-[hsl(150,50%,12%)] to-[hsl(150,40%,8%)]" />

            <motion.nav
              className="relative flex flex-col justify-center h-full px-8 pt-20 pb-32"
              variants={menuContainerVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              {navLinks.map((link, i) => {
                const isActive = location === link.href;
                const Icon = link.icon;
                return (
                  <motion.div key={link.href} variants={menuItemVariants}>
                    <Link href={link.href}>
                      <div
                        className="group flex items-center gap-4 py-3.5 cursor-pointer"
                        onClick={() => setMobileOpen(false)}
                        data-testid={`link-mobile-${link.label.toLowerCase()}`}
                      >
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                            isActive ? "bg-white/20" : "bg-white/5"
                          }`}
                        >
                          <Icon
                            className={`w-5 h-5 ${isActive ? "text-emerald-300" : "text-white/50"}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span
                            className={`block font-display font-semibold text-lg ${
                              isActive ? "text-white" : "text-white/80"
                            }`}
                          >
                            {link.label}
                          </span>
                          <span className="block text-xs text-white/35 mt-0.5">
                            {link.desc}
                          </span>
                        </div>
                        <ArrowRight
                          className={`w-4 h-4 transition-all ${
                            isActive
                              ? "text-emerald-300 opacity-100"
                              : "text-white/20 opacity-0 group-hover:opacity-100"
                          }`}
                        />
                      </div>
                    </Link>
                    {i < navLinks.length - 1 && (
                      <div className="h-px bg-white/5 ml-14" />
                    )}
                  </motion.div>
                );
              })}
            </motion.nav>

            <motion.div
              className="absolute bottom-0 left-0 right-0 px-6 pb-8 pt-4"
              style={{
                background:
                  "linear-gradient(to top, hsl(150,40%,8%) 60%, transparent)",
              }}
              variants={bottomBarVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="flex flex-col gap-3">
                <Link href="/contacto">
                  <Button
                    size="lg"
                    className="w-full bg-white text-[hsl(150,45%,18%)] font-semibold text-base"
                    onClick={() => setMobileOpen(false)}
                    data-testid="button-mobile-cta"
                  >
                    Pedir Presupuesto
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                </Link>
                <div className="flex gap-3">
                  <a
                    href={getWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick("mobile-menu")}
                    className="flex-1"
                    data-testid="button-mobile-whatsapp"
                  >
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full border-white/15 text-white bg-white/5 font-medium"
                    >
                      <SiWhatsapp className="w-5 h-5 mr-2 text-[#25D366]" />
                      WhatsApp
                    </Button>
                  </a>
                  <a
                    href={PHONE_HREF}
                    onClick={() => trackPhoneClick("mobile-menu")}
                    className="flex-1"
                    data-testid="button-mobile-phone"
                  >
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full border-white/15 text-white bg-white/5 font-medium"
                    >
                      <Phone className="w-5 h-5 mr-2 text-emerald-400" />
                      Llamar
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Footer() {
  return (
    <footer className="bg-foreground text-background" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={isotipoSvg} alt="TDMA Logistica" className="w-8 h-8" />
              <span className="font-display font-bold text-lg">
                TDMA Logistica
              </span>
            </div>
            <p className="text-sm opacity-70 leading-relaxed">
              Transporte urgente y estándar en toda España. Recogida rápida en
              cualquier provincia.
            </p>
          </div>

          <div>
            <h3 className="font-display font-semibold mb-3">Servicios</h3>
            <ul className="space-y-2 text-sm opacity-70">
              <li>
                <Link href="/servicio-urgente">
                  <span
                    className="cursor-pointer"
                    data-testid="link-footer-urgente"
                  >
                    Servicio Urgente
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/servicio-estandar">
                  <span
                    className="cursor-pointer"
                    data-testid="link-footer-estandar"
                  >
                    Servicio Estándar
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/fabricantes">
                  <span
                    className="cursor-pointer"
                    data-testid="link-footer-fabricantes"
                  >
                    Para Fabricantes
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-display font-semibold mb-3">Contacto</h3>
            <ul className="space-y-2 text-sm opacity-70">
              <li>
                <a href={PHONE_HREF} data-testid="link-footer-phone">
                  {PHONE_NUMBER}
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} data-testid="link-footer-email">
                  {EMAIL}
                </a>
              </li>
              <li>Horario: L-V 8:00 - 20:00</li>
            </ul>
          </div>

          <div>
            <h3 className="font-display font-semibold mb-3">Legal</h3>
            <ul className="space-y-2 text-sm opacity-70">
              <li>
                <Link href="/aviso-legal">
                  <span
                    className="cursor-pointer"
                    data-testid="link-footer-legal"
                  >
                    Aviso Legal
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/politica-privacidad">
                  <span
                    className="cursor-pointer"
                    data-testid="link-footer-privacy"
                  >
                    Política de Privacidad
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/politica-cookies">
                  <span
                    className="cursor-pointer"
                    data-testid="link-footer-cookies"
                  >
                    Política de Cookies
                  </span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-background/10 text-center text-sm opacity-50">
          <p>
            TDMA Logistica {new Date().getFullYear()}. Todos los derechos
            reservados.
          </p>
          <p className="mt-2">
            Desarrollado por{" "}
            <a
              href="https://seonergy.es"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-80 transition-opacity"
              data-testid="link-seonergy"
            >
              Seonergy
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

function FloatingButtons() {
  const [pulse, setPulse] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setPulse(true);
      setShowTooltip(true);
      setTimeout(() => setPulse(false), 1000);
      setTimeout(() => setShowTooltip(false), 4000);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3 lg:bottom-6 lg:right-6"
      data-testid="floating-buttons"
    >
      <div className="relative" style={{ width: 56, height: 56 }}>
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, x: 10, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 10, scale: 0.9 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              style={{
                position: "absolute",
                right: "calc(100% + 12px)",
                top: "12%",
                transform: "translateY(-50%)",
              }}
              className="whitespace-nowrap bg-white dark:bg-card text-foreground text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg border pointer-events-none"
            >
              <span>¿Te ayudamos? Escríbenos</span>
              <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-white dark:bg-card border-r border-b rotate-[-45deg]" />
            </motion.div>
          )}
        </AnimatePresence>
        <a
          href={getWhatsAppUrl()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackWhatsAppClick("floating")}
          className="relative block w-14 h-14 rounded-full bg-[#25D366] shadow-lg transition-transform hover:scale-105"
          data-testid="button-whatsapp-floating"
          aria-label="WhatsApp"
        >
          {pulse && (
            <>
              <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40" />
              <span className="absolute inset-[-4px] rounded-full border-2 border-[#25D366] animate-ping opacity-30" />
            </>
          )}
          <span className="relative flex items-center justify-center w-full h-full">
            <motion.span
              animate={
                pulse
                  ? { rotate: [0, -12, 14, -8, 10, -4, 0], scale: [1, 1.15, 1] }
                  : {}
              }
              transition={{ duration: 0.8 }}
            >
              <SiWhatsapp className="w-6 h-6 text-white" />
            </motion.span>
          </span>
        </a>
      </div>
      <a
        href={PHONE_HREF}
        onClick={() => trackPhoneClick("floating")}
        className="w-14 h-14 rounded-full bg-primary flex items-center justify-center shadow-lg transition-transform hover:scale-105 lg:hidden"
        data-testid="button-phone-floating"
        aria-label="Llamar"
      >
        <Phone className="w-6 h-6 text-primary-foreground" />
      </a>
    </div>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <FloatingButtons />
    </div>
  );
}
